import { HelpCircle, Clock, ShieldCheck, Layers } from "lucide-react";

interface AuraUpdatedAnswersPanelProps {
  limits?: {
    maxSeconds: number;
    maxRes: '1080p' | '4K';
    maxSizeMB: number;
  };
}

export function AuraUpdatedAnswersPanel({ 
  limits = { maxSeconds: 15, maxRes: '1080p', maxSizeMB: 250 } 
}: AuraUpdatedAnswersPanelProps) {
  return (
    <section className="pt-6">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 p-1 rounded-md bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center">
              <HelpCircle className="h-2.5 w-2.5" />
            </div>
            <h2 className="text-[15px] font-medium tracking-tight text-slate-900">
              Updated Aura.build Answers (with new limits)
            </h2>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-slate-200 bg-slate-50 text-slate-600">
            <Clock className="h-3.5 w-3.5" />
            ~2–3 mins
          </span>
        </div>
        
        <div className="p-4">
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-3 list-decimal pl-5 marker:text-slate-400 text-[13px] text-slate-700">
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Yes — Upload page opens in Empty state; Analysis page defaults to "No analysis yet" with primary Run All Analyses action.
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Formats/limits: Accept MP4/MOV, max {limits.maxSeconds}s, {limits.maxRes} resolution, ≤ {limits.maxSizeMB}MB.
              <ul className="mt-2 list-disc pl-5 space-y-1">
                <li>Codec: H.264/AVC (preferred) and HEVC/H.265 allowed.</li>
                <li>No need for 4K or longer clips; cap enforced at {limits.maxSeconds}s/{limits.maxRes}.</li>
              </ul>
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Progress UI: Show percent + ETA. Include Cancel option (with confirm before abort).
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Metadata: Show filename, duration, resolution, filesize.
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Controls:
              <ul className="mt-2 list-disc pl-5 space-y-1">
                <li>Frame step = ±1 frame (arrow keys + buttons).</li>
                <li>Speeds = 0.25×, 0.5×, 1×, 1.5×, 2×.</li>
              </ul>
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Actions disabled until upload complete and Mux asset ready.
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Show 3–5 recent uploads with thumbnail, date, duration, "View Analysis", and status chips (Processing, Complete, Error).
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Errors: inline top banner on Upload, inline bottom banner on Analysis. Retry clears stale state.
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Header actions: Primary = Run All Analyses. Overflow = Export JSON, Export PDF, Copy Share Link. Optional: Re-run Combined, Open in Library.
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Default tabs:
              <ul className="mt-2 list-disc pl-5 space-y-1">
                <li>Right (top): Video View first if no MediaPipe, otherwise MediaPipe View.</li>
                <li>Bottom: default = Combined tab (with CTA if empty).</li>
              </ul>
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Overlay toolbar: intensity slider 0–100 (default 60). "Hands only" hides all but hand/wrist joints.
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Key Metrics:
              <ul className="mt-2 list-disc pl-5 space-y-1">
                <li>Tempo ratio</li>
                <li>Head/pelvis sway (% body height; cm if calibrated)</li>
                <li>Wrist angles (deg)</li>
                <li>Hip/shoulder rotation + separation (deg)</li>
                <li>Hand path notes</li>
                <li>Shaft lean at impact (deg)</li>
                <li>Low-point control (qualitative)</li>
              </ul>
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Coach Narrative: 2–4 sentences, concise, actionable tone.
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Drills: 3–5 cards, title + 1-liner, "Try it" deep-links to /drills (future).
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Telemetry badges: Latency, Tokens In/Out, Est. $, Model.
              <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[12px] text-slate-600">
                Mock: Latency 3.2s • In 2.1k • Out 0.7k • $0.03 • Model gpt-4.1-mini.
              </div>
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Rating: 1–5 stars + optional notes. Auto-save with toast ("Saved"), no button.
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Coach Feedback drawer: default collapsed, ~380px wide, shows history list (timestamp + author).
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Mobile: Sticky bottom actions on Upload; Analysis collapses bottom content into accordions.
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Sample data: Yes — seed with 2–3 example swings (≤{limits.maxSeconds}s, {limits.maxRes}) and one analysis.
            </li>
            <li className="rounded-lg border border-slate-200 p-3">
              ✅ Export: swing-analysis-YYYYMMDD-HHMM.json/pdf; include video context (club, handedness, lie, duration).
            </li>
          </ol>
          
          <div className="mt-4 flex items-center justify-between">
            <p className="text-[13px] text-slate-600">
              Updated with new limits. Review and confirm if any edits are needed.
            </p>
            <div className="hidden sm:flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-200 bg-slate-50 text-slate-700 text-xs font-medium">
                <ShieldCheck className="h-3.5 w-3.5" />
                WCAG AA ready
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-200 bg-slate-50 text-slate-700 text-xs font-medium">
                <Layers className="h-3.5 w-3.5" />
                Reuses existing DS
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}